// Google Tag Manager / GA4 dataLayer helpers.
// GTM container (GTM-WHCZ9TFV) is loaded once in public/index.html; these
// helpers push standard GA4 ecommerce events so GTM tags/triggers built on
// top of the dataLayer can fire GA4 events without further code changes.
// They also mirror the same events into our own database (see
// postAnalyticsEvent) so the admin dashboard can show real-time funnel
// data without waiting on GA4's reporting latency.

import { v4 as uuidv4 } from "uuid";
import axiosInstance from "../utils/axios";

const DEFAULT_CURRENCY = "INR";
const SESSION_ID_KEY = "artote_analytics_session_id";

const getSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

// Fire-and-forget: analytics must never block the UI or surface errors to the user.
const postAnalyticsEvent = (eventType, payload = {}) => {
  axiosInstance
    .post("/analytics/track", { eventType, sessionId: getSessionId(), ...payload })
    .catch(() => { });
};

const getDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

export const pushToDataLayer = (data) => {
  getDataLayer().push(data);
};

export const trackPageView = (path, title) => {
  pushToDataLayer({
    event: "page_view",
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href
  });
};

// Mirrors every page visit (including the very first hard load, unlike
// trackPageView which GTM's base tag already covers) into our own DB so the
// admin Analytics page can show which pages visitors actually go to.
export const trackPageVisit = (path) => {
  postAnalyticsEvent("page_view", { pagePath: path });
};

// Normalizes the many shapes product/cart data takes across the app
// (product docs, cart items, wishlist/compare items) into a single GA4 item.
const toGA4Item = (product = {}, quantity) => {
  const category = Array.isArray(product.category)
    ? product.category[0]
    : product.category;

  const qty = Number(quantity || product.quantity || 1);

  let price =
    product.finalPrice ??
    product.discountPrice ??
    product.price ??
    product.productPrice ??
    0;

  // Some summaries (e.g. checkout line items) only expose a line total.
  if (!price && product.total) {
    price = product.total / qty;
  }

  return {
    item_id: product.id || product._id || product.productId || "",
    item_name: product.name || product.productName || "Product",
    item_category: category || undefined,
    price: +(Number(price) || 0).toFixed(2),
    quantity: qty
  };
};

const clearEcommerce = () => pushToDataLayer({ ecommerce: null });

export const trackViewItem = (product, currency = DEFAULT_CURRENCY) => {
  if (!product) return;
  const item = toGA4Item(product);

  clearEcommerce();
  pushToDataLayer({
    event: "view_item",
    ecommerce: {
      currency,
      value: item.price,
      items: [item]
    }
  });

  postAnalyticsEvent("view_item", {
    productId: item.item_id,
    productName: item.item_name,
    quantity: item.quantity,
    value: item.price,
    currency
  });
};

export const trackAddToCart = (product, quantity = 1, currency = DEFAULT_CURRENCY) => {
  if (!product) return;
  const item = toGA4Item(product, quantity);
  const value = +(item.price * item.quantity).toFixed(2);

  clearEcommerce();
  pushToDataLayer({
    event: "add_to_cart",
    ecommerce: {
      currency,
      value,
      items: [item]
    }
  });

  postAnalyticsEvent("add_to_cart", {
    productId: item.item_id,
    productName: item.item_name,
    quantity: item.quantity,
    value,
    currency
  });
};

export const trackBeginCheckout = (cartItems = [], currency = DEFAULT_CURRENCY) => {
  if (!cartItems.length) return;
  const items = cartItems.map((cartItem) => toGA4Item(cartItem, cartItem.quantity));
  const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  clearEcommerce();
  pushToDataLayer({
    event: "begin_checkout",
    ecommerce: {
      currency,
      value: +value.toFixed(2),
      items
    }
  });

  postAnalyticsEvent("begin_checkout", {
    quantity: items.reduce((sum, item) => sum + item.quantity, 0),
    value: +value.toFixed(2),
    currency
  });
};

export const trackPurchase = (order, currency = DEFAULT_CURRENCY) => {
  if (!order) return;
  const items = (order.items || []).map((item) =>
    toGA4Item(
      {
        id: item.product || item.productId || item.id,
        name: item.productName || item.name,
        category: item.category,
        finalPrice: item.finalPrice
      },
      item.quantity
    )
  );

  clearEcommerce();
  pushToDataLayer({
    event: "purchase",
    ecommerce: {
      transaction_id: order.transactionNumber || order._id || order.id,
      value: Number(order.totalAmount) || 0,
      currency,
      shipping: order.shippingCost || 0,
      coupon: order.couponCode || order.appliedCoupon?.code || undefined,
      items
    }
  });

  const transactionId = order.transactionNumber || order._id || order.id;

  // One event per line item (each carrying a productId) so the admin
  // Analytics page can show per-product purchase counts, not just an order
  // total. The backend dedupes by transactionId when counting "orders" so
  // this doesn't inflate the Purchases KPI.
  items.forEach((item) => {
    postAnalyticsEvent("purchase", {
      transactionId,
      productId: item.item_id,
      productName: item.item_name,
      quantity: item.quantity,
      value: +(item.price * item.quantity).toFixed(2),
      currency
    });
  });
};
